<template>
  <AppSidebar>
    <div class="max-w-4xl mx-auto">
      <!-- Progress Indicator -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-4">
          <h1 class="text-2xl font-bold text-gray-900">Order Service</h1>
          <div class="text-sm text-gray-500">
            Step {{ currentStep }} of {{ totalSteps }}
          </div>
        </div>
        
        <!-- Progress Bar -->
        <div class="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            class="bg-primary-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: (currentStep / totalSteps) * 100 + '%' }"
          ></div>
        </div>
        
        <!-- Step Indicators -->
        <div class="flex justify-between">
          <div 
            v-for="(step, index) in orderSteps" 
            :key="step.name"
            class="flex items-center"
            :class="{ 'opacity-50': index + 1 > currentStep }"
          >
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
              :class="index + 1 <= currentStep 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-200 text-gray-600'"
            >
              {{ index + 1 }}
            </div>
            <span class="ml-2 text-sm font-medium text-gray-700">{{ step.name }}</span>
          </div>
        </div>
      </div>
      
      <!-- Order Form -->
      <div class="card p-8">
        <!-- Step 1: Service Selection -->
        <div v-if="currentStep === 1" class="space-y-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Select Service</h2>
            <p class="text-gray-600 mb-6">Choose the service you'd like to order.</p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div 
              v-for="service in availableServices" 
              :key="service.id"
              @click="selectService(service)"
              class="border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-md"
              :class="orderForm.service_id === service.id 
                ? 'border-primary-600 bg-primary-50' 
                : 'border-gray-200 hover:border-gray-300'"
            >
              <div class="flex items-start justify-between mb-4">
                <h3 class="font-semibold text-gray-900">{{ service.name }}</h3>
                <div class="text-right">
                  <p class="text-sm text-gray-500">Starting from</p>
                  <p class="font-bold text-primary-600">${{ service.pricing.starting_price.toLocaleString() }}</p>
                </div>
              </div>
              <p class="text-gray-600 text-sm mb-4">{{ service.short_description }}</p>
              
              <!-- Package Selection -->
              <div v-if="orderForm.service_id === service.id && service.packages" class="mt-4 pt-4 border-t border-gray-200">
                <p class="font-medium text-gray-900 mb-3">Select Package:</p>
                <div class="space-y-2">
                  <label 
                    v-for="pkg in service.packages" 
                    :key="pkg.name"
                    class="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                    :class="orderForm.package === pkg.name ? 'border-primary-600 bg-primary-50' : 'border-gray-200'"
                  >
                    <input 
                      type="radio" 
                      :value="pkg.name" 
                      v-model="orderForm.package"
                      class="text-primary-600 focus:ring-primary-500"
                    />
                    <div class="ml-3 flex-1">
                      <div class="flex items-center justify-between">
                        <span class="font-medium text-gray-900">{{ pkg.name }}</span>
                        <span class="font-bold text-gray-900">${{ pkg.price.toLocaleString() }}</span>
                      </div>
                      <div class="text-sm text-gray-600 mt-1">
                        {{ pkg.features.slice(0, 2).join(', ') }}
                        <span v-if="pkg.features.length > 2">...</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Step 2: Requirements -->
        <div v-if="currentStep === 2" class="space-y-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Project Requirements</h2>
            <p class="text-gray-600 mb-6">Tell us about your project in detail.</p>
          </div>
          
          <div class="space-y-6">
            <div>
              <label class="form-label">Project Description *</label>
              <textarea
                v-model="orderForm.requirements.project_description"
                rows="5"
                class="form-input"
                placeholder="Describe your project, goals, and what you're looking to achieve..."
                required
              ></textarea>
            </div>
            
            <div>
              <label class="form-label">Specific Features Needed</label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <label 
                  v-for="feature in commonFeatures" 
                  :key="feature"
                  class="flex items-center"
                >
                  <input 
                    type="checkbox" 
                    :value="feature" 
                    v-model="orderForm.requirements.features_needed"
                    class="text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span class="ml-2 text-gray-700">{{ feature }}</span>
                </label>
              </div>
            </div>
            
            <div>
              <label class="form-label">Reference Links or Examples</label>
              <textarea
                v-model="orderForm.requirements.reference_links"
                rows="3"
                class="form-input"
                placeholder="Share any websites, designs, or examples that inspire your project..."
              ></textarea>
            </div>
            
            <div>
              <label class="form-label">Special Requests or Notes</label>
              <textarea
                v-model="orderForm.requirements.special_requests"
                rows="3"
                class="form-input"
                placeholder="Any additional requirements, preferences, or constraints..."
              ></textarea>
            </div>
          </div>
        </div>
        
        <!-- Step 3: Budget & Timeline -->
        <div v-if="currentStep === 3" class="space-y-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Budget & Timeline</h2>
            <p class="text-gray-600 mb-6">Help us understand your budget and timeline expectations.</p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="form-label">Budget Range *</label>
              <select v-model="orderForm.requirements.budget_range" class="form-input" required>
                <option value="">Select budget range</option>
                <option value="under-5k">Under $5,000</option>
                <option value="5k-15k">$5,000 - $15,000</option>
                <option value="15k-50k">$15,000 - $50,000</option>
                <option value="50k-100k">$50,000 - $100,000</option>
                <option value="over-100k">Over $100,000</option>
              </select>
            </div>
            
            <div>
              <label class="form-label">Preferred Timeline *</label>
              <select v-model="orderForm.requirements.timeline_preference" class="form-input" required>
                <option value="">Select timeline</option>
                <option value="2-4 weeks">2-4 weeks</option>
                <option value="1-2 months">1-2 months</option>
                <option value="2-3 months">2-3 months</option>
                <option value="3-6 months">3-6 months</option>
                <option value="6+ months">6+ months</option>
              </select>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="form-label">Priority Level</label>
              <select v-model="orderForm.requirements.priority_level" class="form-input">
                <option value="standard">Standard</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            
            <div>
              <label class="form-label">Payment Preference</label>
              <select v-model="orderForm.requirements.payment_preference" class="form-input">
                <option value="milestone">Milestone-based</option>
                <option value="upfront">50% Upfront</option>
                <option value="completion">Payment on completion</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Step 4: Contact Information -->
        <div v-if="currentStep === 4" class="space-y-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <p class="text-gray-600 mb-6">Confirm your contact details for this project.</p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="form-label">Company Name</label>
              <input
                v-model="orderForm.requirements.company_info.name"
                type="text"
                class="form-input"
                :placeholder="authStore.user?.company || 'Your company name'"
              />
            </div>
            
            <div>
              <label class="form-label">Project Manager/Contact Person *</label>
              <input
                v-model="orderForm.requirements.project_manager"
                type="text"
                class="form-input"
                :placeholder="authStore.user?.name"
                required
              />
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="form-label">Communication Preference</label>
              <select v-model="orderForm.requirements.communication_preference" class="form-input">
                <option value="email">Email</option>
                <option value="phone">Phone calls</option>
                <option value="video">Video meetings</option>
                <option value="slack">Slack/Teams</option>
              </select>
            </div>
            
            <div>
              <label class="form-label">Meeting Availability</label>
              <select v-model="orderForm.requirements.meeting_availability" class="form-input">
                <option value="business-hours">Business hours (9-5)</option>
                <option value="flexible">Flexible schedule</option>
                <option value="evenings">Evenings preferred</option>
                <option value="weekends">Weekends available</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Step 5: Review & Submit -->
        <div v-if="currentStep === 5" class="space-y-6">
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Review & Submit</h2>
            <p class="text-gray-600 mb-6">Please review your order details before submitting.</p>
          </div>
          
          <!-- Order Summary -->
          <div class="bg-gray-50 rounded-lg p-6">
            <h3 class="font-semibold text-gray-900 mb-4">Order Summary</h3>
            
            <div class="space-y-4">
              <div class="flex justify-between">
                <span class="text-gray-600">Service:</span>
                <span class="font-medium">{{ getSelectedServiceName() }}</span>
              </div>
              
              <div v-if="orderForm.package" class="flex justify-between">
                <span class="text-gray-600">Package:</span>
                <span class="font-medium">{{ orderForm.package }}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-gray-600">Budget Range:</span>
                <span class="font-medium">{{ formatBudgetRange(orderForm.requirements.budget_range) }}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-gray-600">Timeline:</span>
                <span class="font-medium">{{ orderForm.requirements.timeline_preference }}</span>
              </div>
              
              <div class="pt-4 border-t border-gray-200">
                <div class="flex justify-between">
                  <span class="font-semibold text-gray-900">Estimated Cost:</span>
                  <span class="font-bold text-primary-600">${{ getEstimatedCost().toLocaleString() }}</span>
                </div>
                <p class="text-sm text-gray-500 mt-1">Final cost will be determined after consultation</p>
              </div>
            </div>
          </div>
          
          <!-- Terms Agreement -->
          <div class="flex items-start space-x-3">
            <input
              id="agree-terms"
              v-model="orderForm.terms_agreement"
              type="checkbox"
              required
              class="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label for="agree-terms" class="text-sm text-gray-700">
              I agree to the 
              <a href="#" class="text-primary-600 hover:text-primary-500">Terms of Service</a> 
              and 
              <a href="#" class="text-primary-600 hover:text-primary-500">Privacy Policy</a>. 
              I understand that this is a service inquiry and final pricing will be provided after consultation.
            </label>
          </div>
          
          <div>
            <label class="form-label">Additional Notes (Optional)</label>
            <textarea
              v-model="orderForm.requirements.additional_notes"
              rows="3"
              class="form-input"
              placeholder="Any final notes or questions..."
            ></textarea>
          </div>
        </div>
        
        <!-- Navigation Buttons -->
        <div class="flex items-center justify-between pt-8 border-t border-gray-200">
          <button
            v-if="currentStep > 1"
            @click="previousStep"
            class="btn btn-outline"
          >
            Previous
          </button>
          <div v-else></div>
          
          <div class="flex space-x-4">
            <button
              @click="saveDraft"
              class="btn btn-ghost"
            >
              Save Draft
            </button>
            
            <button
              v-if="currentStep < totalSteps"
              @click="nextStep"
              :disabled="!canProceed"
              class="btn btn-primary"
            >
              Next Step
            </button>
            
            <button
              v-else
              @click="submitOrder"
              :disabled="!canSubmit || uiStore.loading"
              class="btn btn-primary"
            >
              <span v-if="uiStore.loading">Submitting...</span>
              <span v-else>Submit Order</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useServiceStore } from '@/stores/services'
import { useOrderStore } from '@/stores/orders'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const serviceStore = useServiceStore()
const orderStore = useOrderStore()
const uiStore = useUIStore()

// State
const currentStep = ref(1)
const totalSteps = 5

// Order form data
const orderForm = reactive({
  service_id: null,
  package: '',
  requirements: {
    project_description: '',
    features_needed: [],
    reference_links: '',
    special_requests: '',
    budget_range: '',
    timeline_preference: '',
    priority_level: 'standard',
    payment_preference: 'milestone',
    company_info: {
      name: ''
    },
    project_manager: '',
    communication_preference: 'email',
    meeting_availability: 'business-hours',
    additional_notes: ''
  },
  terms_agreement: false
})

// Order steps
const orderSteps = [
  { name: 'Service', fields: ['service_id', 'package'] },
  { name: 'Requirements', fields: ['requirements.project_description'] },
  { name: 'Budget & Timeline', fields: ['requirements.budget_range', 'requirements.timeline_preference'] },
  { name: 'Contact Info', fields: ['requirements.project_manager'] },
  { name: 'Review', fields: ['terms_agreement'] }
]

// Common features for selection
const commonFeatures = [
  'User Authentication',
  'Payment Processing',
  'Admin Dashboard',
  'Mobile Responsive',
  'API Integration',
  'Database Design',
  'Search Functionality',
  'Email Notifications',
  'File Upload',
  'Real-time Updates',
  'Analytics Integration',
  'SEO Optimization'
]

// Initialize
onMounted(async () => {
  await serviceStore.fetchServices()
  await serviceStore.fetchCategories()
  
  // Pre-fill user information
  if (authStore.user) {
    orderForm.requirements.project_manager = authStore.user.name
    orderForm.requirements.company_info.name = authStore.user.company || ''
  }

  // Pre-select from query parameters
  const serviceUid = route.query.service
  const packageName = route.query.package

  if (serviceUid) {
    const service = serviceStore.getActiveServices().find(s => s.uid === serviceUid)
    if (service) {
      selectService(service)
      if (packageName) {
        orderForm.package = packageName
      }
    }
  }
})

// Computed properties
const availableServices = computed(() => {
  return serviceStore.getActiveServices()
})

const canProceed = computed(() => {
  const step = orderSteps[currentStep.value - 1]
  return step.fields.every(field => {
    const value = getNestedValue(orderForm, field)
    return value !== null && value !== '' && value !== undefined
  })
})

const canSubmit = computed(() => {
  return canProceed.value && orderForm.terms_agreement
})

// Helper functions
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

const selectService = (service) => {
  orderForm.service_id = service.id
  orderForm.package = '' // Reset package selection
}

const getSelectedServiceName = () => {
  const service = serviceStore.getServiceById(orderForm.service_id)
  return service?.name || 'Unknown Service'
}

const formatBudgetRange = (range) => {
  const ranges = {
    'under-5k': 'Under $5,000',
    '5k-15k': '$5,000 - $15,000',
    '15k-50k': '$15,000 - $50,000',
    '50k-100k': '$50,000 - $100,000',
    'over-100k': 'Over $100,000'
  }
  return ranges[range] || range
}

const getEstimatedCost = () => {
  const service = serviceStore.getServiceById(orderForm.service_id)
  if (!service) return 0
  
  if (orderForm.package && service.packages) {
    const pkg = service.packages.find(p => p.name === orderForm.package)
    return pkg?.price || service.pricing.starting_price
  }
  
  return service.pricing.starting_price
}

// Navigation functions
const nextStep = () => {
  if (canProceed.value && currentStep.value < totalSteps) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

const saveDraft = () => {
  // Save to localStorage
  localStorage.setItem('order_draft', JSON.stringify(orderForm))
  
  uiStore.showNotification({
    type: 'success',
    title: 'Draft Saved',
    message: 'Your order draft has been saved.'
  })
}

const submitOrder = async () => {
  if (!canSubmit.value) return
  
  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    uiStore.showNotification({
      type: 'error',
      title: 'Authentication Required',
      message: 'Please log in to submit an order.'
    })
    router.push('/login')
    return
  }
  
  try {
    // Transform the order form to match backend expectations
    const orderData = {
      services: [
        {
          service_id: orderForm.service_id,
          package_id: orderForm.package ? parseInt(orderForm.package) : null,
          quantity: 1,
          customizations: orderForm.requirements.features_needed || []
        }
      ],
      requirements: {
        project_description: orderForm.requirements.project_description,
        budget_range: orderForm.requirements.budget_range,
        timeline_preference: orderForm.requirements.timeline_preference,
        priority_level: orderForm.requirements.priority_level,
        payment_preference: orderForm.requirements.payment_preference,
        company_info: orderForm.requirements.company_info,
        project_manager: orderForm.requirements.project_manager,
        communication_preference: orderForm.requirements.communication_preference,
        meeting_availability: orderForm.requirements.meeting_availability,
        additional_notes: orderForm.requirements.additional_notes,
        reference_links: orderForm.requirements.reference_links,
        special_requests: orderForm.requirements.special_requests
      }
    }
    
    console.log('Submitting order data:', orderData)
    const result = await orderStore.createOrder(orderData)
    
    if (result && result.id) {
      // Clear draft
      localStorage.removeItem('order_draft')
      
      uiStore.showNotification({
        type: 'success',
        title: 'Order Submitted',
        message: 'Your order has been submitted successfully!'
      })
      
      // Redirect to order confirmation or dashboard
      router.push('/orders')
    } else {
      throw new Error('Order submission failed')
    }
  } catch (error) {
    console.error('Failed to submit order:', error)
    uiStore.showNotification({
      type: 'error',
      title: 'Order Failed',
      message: error.message || 'Failed to submit order. Please try again.'
    })
  }
}

// Load draft if exists
const loadDraft = () => {
  const draft = localStorage.getItem('order_draft')
  if (draft) {
    try {
      const parsedDraft = JSON.parse(draft)
      Object.assign(orderForm, parsedDraft)
      
      uiStore.showNotification({
        type: 'info',
        title: 'Draft Loaded',
        message: 'Your previous draft has been loaded.'
      })
    } catch (error) {
      console.error('Failed to load draft:', error)
    }
  }
}

// Load draft on component mount
onMounted(() => {
  loadDraft()
})
</script>
